import * as THREE from 'three';
import * as TSL from 'three/tsl';
import { PointsNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';
import { ISerializableObjectTag } from '../../../utils/serialization/serializable.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { TexturePath } from '../../../utils/serialization/decorator.js';
import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** WebGPU `PointsNodeMaterial` asset: point sprites with color and world-space point size. */
@EngineClass('PointsNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Points Node Material',
  nodeMaterialGroup: 'TSL Node (THREE)',
})
export class PointsNodeMaterialAsset extends NodeMaterialAsset(PointsNodeMaterial) {
  readonly [ISerializableObjectTag] = true as const;

  @property({ type: 'color', description: 'Point color' })
  override color = new THREE.Color(1.0, 1.0, 1.0);

  @property({ type: 'number', min: 0.001, max: 1.0, step: 0.001, description: 'Point size in world units' })
    pointSize = 0.05;

  @property({ type: 'boolean', description: 'Use size attenuation with camera distance' })
  override sizeAttenuation = true;

  @property({ type: 'texturePath', description: 'Point sprite map path' })
    mapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Point sprite alpha map path' })
    alphaMapPath: TexturePath = '';

  private readonly _sizeUniform = TSL.uniform(this.pointSize);

  constructor() {
    super();
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.pointSize = this.clampFiniteNumber(this.pointSize, 0.001, 1.0, 0.05);
    this.syncTexturePath('map', this.mapPath, THREE.SRGBColorSpace, THREE.ClampToEdgeWrapping, THREE.ClampToEdgeWrapping);
    this.syncTexturePath('alphaMap', this.alphaMapPath, THREE.NoColorSpace, THREE.ClampToEdgeWrapping, THREE.ClampToEdgeWrapping);
    this.colorNode = null;
    this._sizeUniform.value = this.pointSize;
    (this as any).sizeNode = this._sizeUniform;
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): PointsNodeMaterialAsset {
    const instance = new PointsNodeMaterialAsset();
    PointsNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
